export const EPC_LEN = 24;

export function resolvePackerName(user) {
  if (!user) return 'Operator';
  if (typeof user === 'string') return user;
  return user.full_name || user.name || user.email || 'Operator';
}

export function mapEadinStatus(statusOrDetails) {
  const details = statusOrDetails && typeof statusOrDetails === 'object' ? statusOrDetails : {};
  const normalized = String(details.status || statusOrDetails || 'OPEN').toUpperCase();
  if (details.receipt_closed_at || details.receiptClosedAt) return 'closed';
  if (normalized === 'CLOSED') return 'closed';
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return 'canceled';
  if (normalized === 'DRAFT') return 'draft';
  return 'open';
}

export const EADIN_STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-800',
  closed: 'bg-black text-white',
  canceled: 'bg-red-200 text-red-900',
  draft: 'bg-gray-200 text-gray-700',
};

export const EADIN_STATUS_LABELS = {
  open: 'Open',
  closed: 'Receipt closed',
  canceled: 'Canceled',
  draft: 'Draft',
};

export function deriveReceivingStage(poDetail) {
  if (!poDetail) return 'expected';
  const eadin = mapEadinStatus(poDetail.status);
  if (eadin === 'closed') return 'closed';
  const expected = poDetail.total_expected || 0;
  const received = poDetail.total_cumulative_scanned || 0;
  if (expected > 0 && received >= expected) return 'fully_received';
  if (received > 0) return 'partially_received';
  return 'expected';
}

const EPC_HEX_RE = /^[0-9A-F]{24}$/;
const SGTIN96_HEADER = '30';

// SGTIN-96 (every Eadin-issued EPC): header 0x30, then filter(3 bits) + partition(3 bits, 0-6).
export function isSgtin96Hex(tag) {
  if (!EPC_HEX_RE.test(tag) || !tag.startsWith(SGTIN96_HEADER)) return false;
  return ((parseInt(tag.slice(2, 4), 16) >> 2) & 7) <= 6;
}

// After a stray read (e.g. a 20-hex foreign tag), find where the next real tag starts.
// Among overlapping candidates, prefer one followed by another valid tag so a "30" inside junk
// doesn't swallow the start of the real tag after it.
function nextTagStart(run, from) {
  let fallback = -1;
  for (let j = run.indexOf(SGTIN96_HEADER, from); j >= 0 && j + EPC_LEN <= run.length; j = run.indexOf(SGTIN96_HEADER, j + 1)) {
    if (fallback >= 0 && j >= fallback + EPC_LEN) break;
    if (!isSgtin96Hex(run.slice(j, j + EPC_LEN))) continue;
    const next = j + EPC_LEN;
    if (next + EPC_LEN > run.length || isSgtin96Hex(run.slice(next, next + EPC_LEN))) return j;
    if (fallback < 0) fallback = j;
  }
  return fallback;
}

// The Zebra wedge streams reads back-to-back with no separator, so fixed 24-char slicing
// shifts every tag after a read that isn't 24 hex long. Walk the stream and re-sync instead.
export function parseEpcStream(raw) {
  if (!raw) return [];
  const runs = String(raw).toUpperCase().split(/[^0-9A-F]+/).filter(Boolean);
  const tags = [];
  runs.forEach((run) => {
    let i = 0;
    while (i >= 0 && i + EPC_LEN <= run.length) {
      const chunk = run.slice(i, i + EPC_LEN);
      if (isSgtin96Hex(chunk)) {
        tags.push(chunk);
        i += EPC_LEN;
      } else {
        i = nextTagStart(run, i + 1);
      }
    }
  });
  return tags;
}

export function detectEpcCounts(raw) {
  const tags = parseEpcStream(raw);
  if (tags.length === 0) return null;
  return { total: tags.length, unique: new Set(tags).size };
}

export function extractUniqueEpcTags(raw) {
  return Array.from(new Set(parseEpcStream(raw)));
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString();
}

export function formatDateTimeLocal(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export function sessionResult(log) {
  const expected = (log.items || []).reduce((s, i) => s + (i.expected_quantity || 0), 0);
  const matched = log.total_matched || 0;
  if (matched === expected) return 'complete';
  if (matched < expected) return 'short';
  if (matched > expected) return 'over';
  return 'complete';
}

export const RESULT_BADGE = {
  complete: 'bg-green-100 text-green-800',
  short: 'bg-amber-100 text-amber-800',
  over: 'bg-blue-100 text-blue-800',
  duplicate_epc: 'bg-red-100 text-red-800',
};
